import { estimateSpeechDurationSeconds } from "./playbackBuffering";

export type PlaybackKind = "cached" | "neutral" | "emotion";
export interface PlaybackClip {
  text: string;
  emotional: boolean;
  cached: boolean;
  provider?: string;
  silenceMs?: number;
}
export interface PlaybackLoad {
  audio: AudioBuffer;
  persisted: boolean;
  completion?: Promise<AudioBuffer>;
}
export interface PlaybackProgress { percent: number; ready: number; total: number; estimatedWaitSeconds: number; batchWaiting?:boolean }

/** Session-only measurements contain timings, never screenplay text or tokens. */
export class PlaybackEstimates {
  private samples: Record<PlaybackKind, number[]> = { cached: [], neutral: [], emotion: [] };
  observe(kind: PlaybackKind, elapsed: number, duration: number) {
    if (!Number.isFinite(elapsed) || elapsed <= 0 || !Number.isFinite(duration) || duration <= 0) return;
    const samples = this.samples[kind];
    samples.push(elapsed / Math.max(1, duration));
    if (samples.length > 12) samples.shift();
  }
  seconds(kind: PlaybackKind, duration: number) {
    const samples = [...this.samples[kind]].sort((a, b) => a - b);
    // Conservative cold-start priors; replace them with recent observed service
    // times (including queue, transfer and decode), not a constant startup wait.
    const ratio = samples.length ? samples[Math.ceil((samples.length - 1) * 0.9)] * 1.35
      : kind === "cached" ? 0.08 : kind === "emotion" ? 3 : 1.5;
    const overhead = kind === "cached" ? 0.15 : samples.length ? 2 : kind === "emotion" ? 8 : 3;
    return Math.max(kind === "cached" ? 0.3 : 2, duration * ratio + overhead);
  }
}

interface State {
  cached: boolean;
  duration: number;
  exactDuration: boolean;
  loaded: boolean;
  audio?: AudioBuffer;
  started?: number;
  error?: unknown;
  retryAt?: number;
  deferrals?: number;
  queuePolls?: number;
  completion?: Promise<AudioBuffer>;
  batchWaiting?:boolean;
}
export const PLAYBACK_PROGRESS_DELAY_MS = 5000;
// Resource limits only: neither is a required amount of audio before playback.
const DECODE_WINDOW = 32;
const DOWNLOAD_CONCURRENCY = 4;
const GENERATION_CONCURRENCY = 2;

/** Deadline-aware startup, separate download/generation lanes, bounded decoded
 * memory, and a rolling queue. The GPU estimate assumes serial throughput until
 * audio actually completes, even though two requests may be in flight. */
export class AdaptivePlayback {
  private states: State[];
  private cursor = 0;
  private stopped = false;
  private downloads = 0;
  private generations = 0;
  private geminiCooldown = 0;
  private listeners = new Set<() => void>();
  constructor(private clips: PlaybackClip[], private prepare: (index: number) => Promise<PlaybackLoad>,
    private estimates: PlaybackEstimates, private now: () => number = () => performance.now() / 1000) {
    this.states = clips.map(clip => ({ cached: clip.cached, duration: clip.silenceMs !== undefined ? clip.silenceMs / 1000 : estimateSpeechDurationSeconds(clip.text),
      exactDuration: clip.silenceMs !== undefined, loaded: false }));
  }
  stop() { this.stopped = true; this.states.forEach(s => { s.audio = undefined; }); this.wake(); }
  private wake() { this.listeners.forEach(fn => fn()); this.listeners.clear(); }
  private async changed() {
    if (this.stopped) return;
    await new Promise<void>(resolve => {
      const finish = () => { clearTimeout(timer); this.listeners.delete(finish); resolve(); };
      const timer = setTimeout(finish, 100);
      this.listeners.add(finish);
    });
  }
  private kind(index: number): PlaybackKind {
    return this.states[index].cached ? "cached" : this.clips[index].emotional ? "emotion" : "neutral";
  }
  private pump() {
    if (this.stopped || this.states.some(s => s.error)) return;
    // Cached audio near the playhead is never trapped behind a slow GPU request.
    for (let i = this.cursor; i < Math.min(this.states.length, this.cursor + DECODE_WINDOW); i++) {
      const s = this.states[i];
      if (s.cached && !s.audio && s.started === undefined && !s.error && this.canRetry(i) && this.downloads < DOWNLOAD_CONCURRENCY) this.launch(i, false);
    }
    // Start missing renditions in playback order, including emotional passages
    // far beyond the download window. Persisted far-ahead buffers are released.
    for (let i = this.cursor; i < this.states.length && this.generations < GENERATION_CONCURRENCY; i++) {
      const s = this.states[i];
      if (!s.cached && !s.audio && s.started === undefined && !s.error && this.canRetry(i)) this.launch(i, true);
    }
  }
  private retryTime(index: number) {
    return Math.max(this.states[index].retryAt ?? 0,
      !this.states[index].cached && this.clips[index].provider === "gemini" ? this.geminiCooldown : 0);
  }
  private canRetry(index: number) { return this.now() >= this.retryTime(index); }
  private launch(index: number, generation: boolean) {
    const state = this.states[index], kind = this.kind(index);
    state.started = this.now();
    if (generation) this.generations++; else this.downloads++;
    void this.prepare(index).then(result => {
      if (this.stopped) return;
      if (!Number.isFinite(result.audio.duration) || result.audio.duration <= 0) throw new Error("Invalid playback audio.");
      this.estimates.observe(kind, this.now() - state.started!, result.audio.duration);
      state.duration = result.audio.duration; state.exactDuration = true; state.loaded = true;
      state.cached = result.persisted;
      if(result.completion) {
        state.loaded=false;state.exactDuration=false;
        state.completion=result.completion;
        void result.completion.then(final=>{
          if(this.stopped)return;
          if(!Number.isFinite(final.duration)||final.duration<result.audio.duration)throw new Error('Invalid stream completion.');
          state.duration=final.duration;state.exactDuration=true;state.loaded=true;state.cached=true;
          this.wake();
        }).catch(error=>{if(!this.stopped){state.error=error;this.wake();}});
      }
      if (index < this.cursor + DECODE_WINDOW || !result.persisted) state.audio = result.audio;
      // A storage outage must not allow an unbounded full-script PCM buffer.
      if (this.states.filter(s => s.audio).length > DECODE_WINDOW + DOWNLOAD_CONCURRENCY + GENERATION_CONCURRENCY) {
        throw new Error("Audio could not be saved for continuous playback. Please retry when storage is available.");
      }
    }).catch(error => {
      if (this.stopped) return;
      const code = error && typeof error === "object" && "code" in error ? error.code : undefined;
      const storageWait = ["GEMINI_STORAGE_PENDING", "GEMINI_STORAGE_UNAVAILABLE"].includes(String(code));
      if (code === 'GEMINI_QUEUED'||code==='GEMINI_BATCH_QUEUED'||code==='GEMINI_RECOVERING') {
        // Read-only queue polling, not another generation attempt. A busy fair
        // queue can take longer than provider retry limits; Stop still cancels waiting.
        state.queuePolls=(state.queuePolls??0)+1;
        state.batchWaiting=code==='GEMINI_BATCH_QUEUED';
        state.cached=false;
        // The opening clip can expose a verified streaming prefix quickly.
        // Only that playhead lookup polls at 1s; background look-ahead retains
        // bounded backoff, and Batch never receives rapid polling.
        state.retryAt=this.now()+(index===this.cursor&&!state.batchWaiting?1:Math.min(10,2+state.queuePolls));
        return;
      }
      if (["GEMINI_RATE_LIMIT", "GEMINI_PENDING", "GEMINI_UNAVAILABLE"].includes(String(code)) || storageWait) {
        if ((state.deferrals ?? 0) >= 12) {
          state.error = new Error(`Playback is waiting at “${this.clips[index].text.slice(0, 100)}”. ${error instanceof Error ? error.message : "Audio is unavailable."} Please try playback again shortly.`);
          return;
        }
        // These are acknowledged deferrals, not permission to repeat an
        // ambiguous paid call. The server still enforces per-key attempt/budget caps.
        state.deferrals = (state.deferrals ?? 0) + 1;
        state.cached = false;
        state.retryAt = this.now() + (storageWait ? code === "GEMINI_STORAGE_PENDING" ? 2 : 5 : code === "GEMINI_UNAVAILABLE" ? Math.min(280, 70 * 2 ** (state.deferrals - 1)) : 70);
        if (!storageWait && code !== "GEMINI_PENDING") this.geminiCooldown = Math.max(this.geminiCooldown, state.retryAt);
      } else {
        state.error = new Error(`Playback stopped at “${this.clips[index].text.slice(0, 100)}”. ${error instanceof Error ? error.message : "Audio is unavailable."}`);
      }
    }).finally(() => {
      state.started = undefined;
      if (generation) this.generations--; else this.downloads--;
      this.wake(); this.pump();
    });
  }
  /** Maximum projected completion minus the time playback reaches that clip. */
  startupDelaySeconds() {
    let lead = 0, generationTime = 0, delay = 0;
    const downloadLanes = Array<number>(DOWNLOAD_CONCURRENCY).fill(0);
    for (let i = this.cursor; i < this.states.length; i++) {
      const s = this.states[i];
      if (!s.audio) {
        let remaining = this.estimates.seconds(this.kind(i), s.duration);
        if (s.started !== undefined) {
          const elapsed = Math.max(0, this.now() - s.started);
          // Overdue work must not count as finished just because its ETA elapsed.
          remaining = Math.max(remaining - elapsed, Math.min(1, remaining) + remaining * 0.25,
            elapsed > remaining ? elapsed * 0.5 : 0.1);
        }
        if (!s.cached) {
          generationTime = Math.max(generationTime, this.retryTime(i) - this.now()) + remaining;
          delay = Math.max(delay, generationTime + 0.5 - lead);
        } else if (i < this.cursor + DECODE_WINDOW) {
          // Concurrency helps different downloads overlap; it cannot make one
          // slow download finish four times faster.
          const lane = downloadLanes.indexOf(Math.min(...downloadLanes));
          downloadLanes[lane] = Math.max(downloadLanes[lane], this.retryTime(i) - this.now()) + remaining;
          delay = Math.max(delay, downloadLanes[lane] - lead);
        }
      }
      // Unknown speaking speed should not overestimate how much lead we have.
      lead += s.duration * (s.exactDuration ? 1 : 0.8);
    }
    return Math.max(0, delay);
  }
  progress(): PlaybackProgress {
    const ready = this.states.filter(s => s.loaded).length, total = this.states.length;
    return { ready, total, percent: total ? Math.floor(ready * 100 / total) : 0,
      // A queued response has no trustworthy completion deadline. Keep waiting
      // and polling under the existing limits, but don't invent a short ETA.
      estimatedWaitSeconds: this.states.some(s=>(s.batchWaiting||(s.queuePolls??0)>0)&&!s.loaded)?0:Math.ceil(this.startupDelaySeconds()),
      ...(this.states.some(s=>s.batchWaiting&&!s.loaded)?{batchWaiting:true}:{}) };
  }
  private check() {
    if (this.stopped) throw new Error("Playback stopped.");
    const failed = this.states.find(s => s.error);
    if (failed) throw failed.error;
  }
  async waitForStart(onProgress: (progress: PlaybackProgress) => void) {
    this.pump();
    while (this.states.length) {
      this.pump();
      this.check(); onProgress(this.progress());
      if (this.states[0].audio && (this.states[0].completion || this.startupDelaySeconds() === 0)) return;
      await this.changed();
    }
  }
  async get(index: number, onProgress: (progress: PlaybackProgress) => void) {
    this.cursor = index; this.pump();
    while (true) {
      this.pump();
      this.check();
      if (this.states[index].audio) return this.states[index].audio!;
      onProgress(this.progress()); await this.changed();
    }
  }
  release(index: number) { this.states[index].audio = undefined; }
  completion(index:number) {return this.states[index].completion;}
}
